import { EMPTY_COLLECTION_DTL_SEARCH_CRITERIA } from "domain/entity/Collection/CollectionDetailSearchCriteria";
import { CollectionHeaderEntity } from "domain/entity/Collection/CollectionHeaderEntity";
import { CollectionConstant } from "presentation/constant/Collection/CollectionConstant";
import { MessageConstant } from "presentation/constant/MessageConstant";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useCollectionVM } from "presentation/hook/Collection/useCollectionVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useCollectionTracked } from "presentation/store/Collection/CollectionProvider";
import { ConfirmDeleteModal } from "presentation/view/components/ConfirmDeleteModal";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import { memo, useCallback, useState } from "react";
import { HPHButton, IconButton, Loader } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, StyledAction } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const CollectionTitleBar: React.FC = () => {
    const SCREEN_CONSTANT = CollectionConstant;
    const [collectionState] = useCollectionTracked();
    const collectionVM = useCollectionVM();
    const { isShowDetailPanel, selectedRows, isShowEditPanel, collectionHdrList, searchCriteria } = collectionState;
    const [isLoading, setIsLoading] = useState(false);
    const messageBarVM = useMessageBarVM();
    const [isShowDelete, setIsShowDelete] = useState<boolean>(false);
    const [deletedCollectionHdr, setDeletedCollectionHdr] = useState<CollectionHeaderEntity[]>();
    const [anaInfoState] = useANAInfoTracked();
    const { allowUpdate, allowDelete, operatingUnit } = anaInfoState;

    const handleConfirmClick = useCallback(async () => {
        setIsLoading(true);
        let criteria = { ...EMPTY_COLLECTION_DTL_SEARCH_CRITERIA };
        criteria = {
            ...criteria,
            selectRows: selectedRows,
            allRows: collectionHdrList
        }
        await collectionVM.onConfirmHdr(criteria).then(data => {
            if (data) {
                setIsLoading(false)
                messageBarVM.showError(data ?? "Confirm Failed.");
                return;
            } else {
                collectionVM.onSearch(searchCriteria).then(data => {
                    setIsLoading(false);
                    messageBarVM.showSuccess("Confirm successfully.");
                });
            }
        }).catch((error) => {
            setIsLoading(false);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [collectionHdrList, collectionVM, messageBarVM, searchCriteria, selectedRows]);

    const handleReprintClick = useCallback(async () => {
        let rows = [];
        let rptRows = [];
        if (selectedRows.length === 0) {
            rows = collectionHdrList.filter(entity => entity.receiptNo === null || entity.receiptNo === "" || entity.receiptNo === undefined);
            rptRows = collectionHdrList.slice();
        } else {
            rows = selectedRows.filter(entity => entity.receiptNo === null || entity.receiptNo === "" || entity.receiptNo === undefined);
            rptRows = selectedRows.slice();
        }
        if (rows && rows.length > 0) {
            const msg = rows.map(ety => ety.draftInvoiceNo).join(", ").toString();
            messageBarVM.showError(msg + " receipt no.is null,cannot reprint.");
            return;
        }
        if (rptRows.length > 5) {
            messageBarVM.showError("Max. 5 records are allowed to view online, please check the criteria inputted.");
            return;
        }
        setIsLoading(true)
        await collectionVM.onReprint(rptRows).then(data => {
            if (data) {
                setIsLoading(false)
                messageBarVM.showError(data);
                return;
            }
            setIsLoading(false)
        });

    }, [collectionHdrList, collectionVM, messageBarVM, selectedRows]);


    const handleDetailClick = useCallback(async () => {
        setIsLoading(true);
        let criteria = { ...EMPTY_COLLECTION_DTL_SEARCH_CRITERIA };
        criteria = {
            ...criteria,
            selectRows: selectedRows,
            allRows: collectionHdrList
        }
        await collectionVM.onSearchDtl(criteria).then(data => {
            if (data) {
                messageBarVM.showError(data);
            }
            setIsLoading(false);
            collectionVM.resetDefaultSelectedRows();
        });
    }, [collectionHdrList, collectionVM, messageBarVM, selectedRows]);

    const handleSearch = useCallback(async () => {
        collectionVM.onSearchClick();
    }, [collectionVM]);

    const handleDeleteClick = useCallback(async () => {
        setIsShowDelete(true);
        setDeletedCollectionHdr(selectedRows);
    }, [selectedRows]);

    const handleCancel = () => {
        setIsShowDelete(false);
    }

    const handleDelete = () => {
        setIsLoading(true);
        setIsShowDelete(false);
        if (!deletedCollectionHdr) return;

        collectionVM.onDeleteHdrClick(deletedCollectionHdr).then((data) => {
            if (data && data.success) {
                messageBarVM.showSuccess(MessageConstant.common.DELETED_DATA_SUCCESSFUL)
                collectionVM.onSearch(searchCriteria).then(data => {
                    setIsLoading(false);
                });
            } else {
                messageBarVM.showError(data.data ?? "Delete Failed.");
            }
        }).catch((error) => {
            setIsLoading(false);
        }).finally(() => {
            setIsLoading(false);
        });
    }

    return <Sidebarheader style={{ width: '100%', display: "flex", alignItems: "center" }}>
        <HeaderTitle>{SCREEN_CONSTANT.TITLE}</HeaderTitle>
        {(isLoading) && <Loader Indicator="Spinner" size="Medium" />}
        <StyledAction className="tm-animated-wrapper">
            <IconButton fileName='Icon-search' disabled={isShowDetailPanel || isShowEditPanel} size='medium' toolTipText={'Search'} toolTipArrow={false} onClick={handleSearch} />
            {allowUpdate && <HPHButton label={"Detail"} size={"Small"} theme={"Secondary"} disabled={isShowDetailPanel || isShowEditPanel || selectedRows.length === 0} onClick={handleDetailClick} />}
            {allowUpdate && <HPHButton label={"Reprint"} size={"Small"} theme={(operatingUnit === 'SAPT' || operatingUnit === 'KICT') ? "Secondary" : "Primary"} disabled={isShowDetailPanel || isShowEditPanel || selectedRows?.length === 0} onClick={handleReprintClick} />}
            {(operatingUnit === 'SAPT' || operatingUnit === 'KICT')
                && <>
                    {allowDelete && <> <HPHButton label={WorkspaceConstant.Common.BUTTON_DELETE} size={"Small"} theme={"Secondary"} disabled={isShowDetailPanel || isShowEditPanel || selectedRows.length === 0} onClick={handleDeleteClick} />
                        <ConfirmDeleteModal headerText={`${WorkspaceConstant.Common.BUTTON_DELETE} ${SCREEN_CONSTANT.TITLE}`}
                            contentText={`${MessageConstant.common.DELETE_ALERT_TITLE}\n${MessageConstant.common.DELETE_ALERT_MESSAGE}`}
                            visible={isShowDelete}
                            onCancelClick={handleCancel}
                            onDeleteClick={handleDelete}
                        />
                    </>
                    }
                    {allowUpdate && <HPHButton label={WorkspaceConstant.Common.BUTTON_CONFIRM} size={"Small"} theme={"Primary"} disabled={isShowDetailPanel || isShowEditPanel || selectedRows.length === 0} onClick={handleConfirmClick} />}
                </>}
        </StyledAction>
        {collectionState.forceRefresh && <></>}
    </Sidebarheader>
}

export default memo(CollectionTitleBar);